import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/useAuth';
import { adminPing, getDashboard } from '../api/endpoints/admin';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { ToastContainer } from '../components/ui/Toast';
import { NAV_ITEMS, PAGE_META, navBadgeCount } from './nav';
import { getUnassignedCount } from '../api/endpoints/ops';
import { PhoneBanner } from '../features/ownership/PhoneBanner';
import { opsUserInitials, opsUserName, useOpsUser } from '../features/ownership/opsUser';
import styles from './AppShell.module.css';
import { globalBadgeQueryPolicy, staticQueryPolicy } from '../api/queryPolicies';
import { useInvoicePdfCatchup } from '../features/invoices/useInvoicePdfCatchup';
import { usePaymentQueue } from '../features/payments/hooks';
import { HelpButton } from '../components/ui/HelpButton';
import { HelpProvider } from '../help/HelpProvider';
import { useHelp } from '../help/helpApi';

/**
 * Layout shell per the design handoff: fixed 236px dark sidebar + flexible
 * main column (white header bar + scrollable content).
 */
export function AppShell() {
  const { t } = useTranslation();
  const { email, signOut } = useAuth();
  const { pathname } = useLocation();
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const meta =
    PAGE_META[pathname] ??
    (pathname.startsWith('/help/')
      ? PAGE_META['/help']
      : pathname.startsWith('/users/')
      ? { title: 'User profile', subtitle: 'Account, verification and payment details' }
      : pathname.startsWith('/shipments/')
        ? { title: `Shipment #${pathname.split('/')[2]}`, subtitle: 'Trip, documents and payments' }
        : PAGE_META['/']);

  // Phase 0: proves API connectivity end-to-end; also warms the auth chain.
  const ping = useQuery({
    queryKey: ['admin', 'ping'],
    queryFn: adminPing,
    retry: 1,
    ...staticQueryPolicy,
  });

  // Load dashboard metrics for badges
  const dashboard = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: getDashboard,
    ...globalBadgeQueryPolicy,
  });

  const dashboardData = dashboard.data;

  const unassigned = useQuery({
    queryKey: ['admin', 'work', 'unassigned', 'count'],
    queryFn: getUnassignedCount,
    ...globalBadgeQueryPolicy,
  });

  const { opsUser, isManager } = useOpsUser();
  useInvoicePdfCatchup(opsUser?.id);
  const paymentQueue = usePaymentQueue(!isManager);
  const paymentCount =
    (paymentQueue.data?.topStrip.breachedOrders ?? 0) +
    (paymentQueue.data?.topStrip.atRiskOrders ?? 0) +
    (paymentQueue.data?.topStrip.breachedPayouts ?? 0) +
    (paymentQueue.data?.topStrip.atRiskPayouts ?? 0) +
    (paymentQueue.data?.topStrip.breachedClaims ?? 0) +
    (paymentQueue.data?.topStrip.atRiskClaims ?? 0);
  useEffect(() => {
    document.title = paymentCount > 0 ? `(${paymentCount}) DNM Ops` : 'DNM Ops';
  }, [paymentCount]);
  const displayName = opsUser ? opsUserName(opsUser) : (email ?? 'Ops Admin');
  const initials = opsUser ? opsUserInitials(opsUser) : (email ?? 'OP').slice(0, 2).toUpperCase();
  const roleLabel = opsUser ? (isManager ? 'OPS MANAGER' : 'OPS EXECUTIVE') : 'Role unavailable';

  const badgeCounts = {
    stopped: dashboardData?.stoppedCount ?? 0,
    documents: dashboardData?.pendingDocumentsCount ?? 0,
    unassigned: unassigned.data ?? 0,
    payments: paymentCount,
  };
  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return (
    <HelpProvider pageTitle={meta.title}>
    <div className={styles.layout}>
      <aside className={styles.sidebar}>
        <div className={styles.logoRow}>
          <img className={styles.logoMark} src="/dnm-wordmark-on-dark.svg" alt={t('app.appShell.logoAlt')} />
          <div className={styles.logoMeta}>
            <div className={styles.logoTitle}>{t('app.appShell.logoTitle')}</div>
          </div>
        </div>

        <nav className={styles.nav}>
          {NAV_ITEMS.filter((item) => !item.managerOnly || isManager).map((item) => {
            const count = navBadgeCount(item, badgeCounts);

            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  isActive ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem
                }
              >
                <span>{item.label}</span>
                {count > 0 && (
                  <span
                    className={styles.badge}
                    style={{ background: item.badgeColor }}
                    aria-label={`${count} ${item.label.toLowerCase()}`}
                  >
                    {count}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className={styles.footer}>
          <div className={styles.avatar}>{initials}</div>
          <div className={styles.footerText}>
            <div className={styles.footerName} title={email ?? undefined}>
              {displayName}
            </div>
            <div className={styles.footerRole}>{roleLabel}</div>
          </div>
          <button
            type="button"
            className={styles.signOut}
            onClick={() => setConfirmSignOut(true)}
            aria-label={t('app.appShell.signOut')}
          >
            <svg
              className={styles.signOutIcon}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M5 21q-.825 0-1.412-.587T3 19V5q0-.825.588-1.412T5 3h6q.425 0 .713.288T12 4t-.288.713T11 5H5v14h6q.425 0 .713.288T12 20t-.288.713T11 21zm12.175-8H10q-.425 0-.712-.288T9 12t.288-.712T10 11h7.175L15.3 9.125q-.275-.275-.275-.675t.275-.7t.7-.313t.725.288L20.3 11.3q.3.3.3.7t-.3.7l-3.575 3.575q-.3.3-.712.288t-.713-.313q-.275-.3-.262-.712t.287-.688z" />
            </svg>
            <span>{t('app.appShell.signOut')}</span>
          </button>
        </div>
      </aside>

      <div className={styles.main}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.pageTitle}>{meta.title}</h1>
            <div className={styles.pageSubtitle}>{meta.subtitle}</div>
          </div>
          <div className={styles.headerRight}>
            {!pathname.startsWith('/help') && <PageHelpButton page={meta.title} />}
            <span
              className={`${styles.apiStatus} ${
                ping.isSuccess ? styles.apiOnline : ping.isError ? styles.apiOffline : ''
              }`}
            >
              {ping.isSuccess ? 'API connected' : ping.isError ? 'API unreachable' : 'Connecting'}
            </span>
            <span className={styles.date}>{today}</span>
          </div>
        </header>
        <PhoneBanner />
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
      <ConfirmDialog
        isOpen={confirmSignOut}
        title={t('app.appShell.signOutConfirmTitle')}
        description="Unsaved changes on this page will be lost."
        confirmText="Sign out"
        cancelText="Stay"
        help="sign-out"
        onConfirm={() => {
          setConfirmSignOut(false);
          signOut();
        }}
        onCancel={() => setConfirmSignOut(false)}
      />
      <ToastContainer />
    </div>
    </HelpProvider>
  );
}

/** The header "?": help for the page on screen (also the "?" key). */
function PageHelpButton({ page }: { page: string }) {
  const { t } = useTranslation();
  const { openHelp } = useHelp();
  return <HelpButton label={t('help.buttonLabel', { page })} text={t('help.nav')} onClick={() => openHelp()} />;
}
